/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColDef,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { cloneDeep, groupBy, isEqual, keyBy } from 'lodash-es';
import { combineLatest, EMPTY, firstValueFrom, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import {
  ActivityType,
  AmountType,
  BudgetType,
  Currency,
  DiscountType,
  DriverType,
  EntityType,
  EventType,
  ForecastMethodType,
  GqlService,
  listDriverCustomGroupsWithDataQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { ForecastTableGridPeriodTimeModalComponent } from './components/forecast-table-grid-period-time-modal/forecast-table-grid-period-time-modal.component';
import {
  ForecastTableGridDataInterface,
  ForecastTableGridDriverSelectMode,
  ForecastTableGridExpenses,
  ForecastTableGridMethodSelectMode,
  ForecastTableGridParentComponentContext,
  ForecastTableGridSaveChange,
  ForecastTableGridSiteCurveInterface,
  ForecastTableGridTimelineMilestoneInterface,
  ForecastTableGridTimelinePhaseInterface,
} from './models/forecast-table-grid.model';
import { ForecastTableGridCategoryCheckService } from './services/forecast-table-grid-category-check.service';
import { ForecastTableGridCategoryLoggingService } from './services/forecast-table-grid-category-loading.service';
import { ForecastTableGridCategoryToggleService } from './services/forecast-table-grid-category-toggle.service';
import { ForecastTableGridCategoryService } from './services/forecast-table-grid-category.service';
import { ForecastTableGridColumnsService } from './services/forecast-table-grid-columns.service';
import { ForecastTableGridDriverService } from './services/forecast-table-grid-driver.service';
import { ForecastTableGridMethodService } from './services/forecast-table-grid-method.service';
import { ForecastTableGridPeriodService } from './services/forecast-table-grid-period.service';
import { ForecastTableGridService } from './services/forecast-table-grid.service';
import { ActivityQuery } from '../../state/activity/activity.query';
import {
  CategoryQuery,
  FullActivity,
  FullCategory,
  FullSettings,
} from '../../state/category/category.query';
import { CategoryService } from '../../state/category/category.service';
import { ForecastQuery } from '../../state/forecast/forecast.query';
import { ForecastStore } from '../../state/forecast/forecast.store';
import { ForecastSettingsQuery } from '../../state/settings/forecast-settings.query';
import { ForecastSettingsService } from '../../state/settings/forecast-settings.service';
import { ForecastSettingsStore } from '../../state/settings/forecast-settings.store';

@Component({
  imports: [AgGridAngular, StickyGridDirective, AsyncPipe],
  selector: 'aux-forecast-table-grid',
  styleUrls: ['./forecast-table-grid.component.scss'],
  templateUrl: './forecast-table-grid.component.html',
})
export class ForecastTableGridComponent implements OnInit {
  checkService = inject(ForecastTableGridCategoryCheckService);
  driverService = inject(ForecastTableGridDriverService);
  gridService = inject(ForecastTableGridService);
  loadingService = inject(ForecastTableGridCategoryLoggingService);
  methodService = inject(ForecastTableGridMethodService);
  organizationQuery = inject(OrganizationQuery);
  periodService = inject(ForecastTableGridPeriodService);
  toggleService = inject(ForecastTableGridCategoryToggleService);

  private activityQuery = inject(ActivityQuery);
  private categoryQuery = inject(CategoryQuery);
  private categoryService = inject(CategoryService);
  private ColumnsService = inject(ForecastTableGridColumnsService);
  private destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private forecastQuery = inject(ForecastQuery);
  private forecastSettingsQuery = inject(ForecastSettingsQuery);
  private forecastSettingsService = inject(ForecastSettingsService);
  private forecastSettingsStore = inject(ForecastSettingsStore);
  private forecastStore = inject(ForecastStore);
  private forecastTableGridCategoryService = inject(ForecastTableGridCategoryService);
  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);

  customCurves = signal<listDriverCustomGroupsWithDataQuery[]>([]);

  budgetId = '';
  @Input() changeDiscountTotalAmount!: FormControl;
  @Input() changeDiscountTotalPercent!: FormControl;
  cloneRowData: ForecastTableGridDataInterface[] = [];
  columnDefs: ColDef[];
  context: ForecastTableGridParentComponentContext;
  defaultSubSettings = new Map<string, FullSettings>();
  discountableTotal = 0;
  discountTypes = [
    { id: DiscountType.DISCOUNT_PERCENTAGE, label: '% of Relevant Services' },
    { id: DiscountType.DISCOUNT_TOTAL, label: '$ of Relevant Services' },
  ];
  DriverType = DriverType;
  edits = new Set<string>();
  @Output() emitChangeDiscountTotalAmount: EventEmitter<number> = new EventEmitter<number>();
  @Output()
  emitChangeDiscountTotalAmountEnable: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitChangeDiscountTotalPercent: EventEmitter<number> = new EventEmitter<number>();
  @Output()
  emitChangeDiscountTotalPercentEnable: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitHideDiscounts: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitIsPatientDriversLinkVisible: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitIsSiteDriversLinkVisible: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitSaveCheck: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitSuccessForecast: EventEmitter<boolean> = new EventEmitter<boolean>();
  expensesAmount: ForecastTableGridExpenses = { amount: 0, amountPercent: 0 };
  gridApi?: GridApi;
  gridHeight = '400px';
  gridOptions: GridOptions;
  @Output() gridValueChange = new EventEmitter<ForecastTableGridDataInterface[]>();
  groupRowCount = 0;
  initialSettings: Record<string, FullSettings | undefined> = {};
  @Input() isClosedMonthsProcessing = false;
  @Input() isCustomCurveAvailable = false;
  @Input() isForecastFinalized = false;
  @Input() isPatientDriverAvailable = false;
  @Input() isPatientDriversLinkVisible = false;
  @Input() isSiteDriverAvailable = false;
  @Input() isSiteDriversLinkVisible = false;
  patientCurves: EditableListDropdownItem[] = [];
  rowData: ForecastTableGridDataInterface[] = [];
  @Input() saveCheck = false;
  selectedDiscountType = new FormControl({
    id: DiscountType.DISCOUNT_PERCENTAGE,
    label: '% of Relevant Services',
  });
  @Input() selectedVendor!: FormControl;
  siteCurves: ForecastTableGridSiteCurveInterface[] = [];
  subscription!: Subscription;
  @Input() successForecast = false;
  timelineMilestones: ForecastTableGridTimelineMilestoneInterface[] = [];
  timelinePhases: ForecastTableGridTimelinePhaseInterface[] = [];
  userHasModifyPermissions = false;

  // eslint-disable-next-line max-lines-per-function
  ngOnInit(): void {
    // Get row data
    combineLatest([
      this.gridService.getRowData(),
      this.forecastTableGridCategoryService.getActivities(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([[serviceCategories, costCategories], activities]) => {
        this.rowData = [...activities, ...serviceCategories, ...costCategories];
        const a = groupBy(this.rowData, 'costCategoryType');

        this.groupRowCount = Object.keys(a).length;

        if (
          this.cloneRowData.length === 0 ||
          this.cloneRowData?.[0]?.id !== this.rowData?.[0]?.id
        ) {
          this.cloneRowData = Utils.clone(this.rowData);
        }

        this.onRowResize();
        this.gridValueChange.emit(this.rowData);
      });

    // Get site curves
    this.gridService
      .getSiteCurves()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((siteCurves) => {
        this.siteCurves = siteCurves;
        this.forecastStore.update({
          hasSiteCurves: !!siteCurves.length,
        });
      });

    // Get patient curves
    this.gridService
      .getPatientCurves()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((patientCurves) => {
        this.patientCurves = patientCurves;
        this.forecastStore.update({
          hasPatientCurves: !!patientCurves.length,
        });
      });

    this.gridService.getCustomCurves().subscribe((curves) => {
      this.customCurves.set(curves);
      this.forecastStore.update({
        hasCustomCurves: !!curves.length,
      });
    });

    this.gridService
      .getTimelineItems() // Get timeline items
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([timelinePhases, timelineMilestones]) => {
        this.timelinePhases = timelinePhases;
        this.timelineMilestones = timelineMilestones;
      });

    // Get user auth
    this.gridService
      .getAuthorization()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userHasModifyPermissions) => {
        this.userHasModifyPermissions = userHasModifyPermissions;
        this.gridApi?.refreshCells();
      });

    // Get budget
    this.getListBudgetVersionExpenses();

    // Listen for discount changes
    this.selectedDiscountType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.processDiscountTypeChanges(value);
      });

    // Listen for change discount changes
    this.gridService
      .getChangeDiscountChanges(
        this.changeDiscountTotalAmount.valueChanges,
        this.changeDiscountTotalPercent.valueChanges
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.processChangeDiscountChanges();
      });
  }

  afterActivityStateChange(activityId: string): void {
    const needFlag = this.checkService.checkIfActivityHasError(
      activityId,
      this.isPatientDriverAvailable,
      this.isSiteDriverAvailable,
      this.isCustomCurveAvailable
    );
    const entity = this.activityQuery.getEntity(activityId);
    const activity = this.checkService.getActivityType(entity);

    if (!activity || !activity.primary_settings_id) {
      return;
    }

    const hasFlag = !!this.forecastSettingsQuery.ui.getEntity(activity.primary_settings_id)
      ?.unforecasted;

    if (hasFlag !== needFlag) {
      this.forecastSettingsStore.ui.update(activity.primary_settings_id, {
        unforecasted: needFlag,
      });

      this.afterCategoryStateChange(activity.category_id);
    }

    this.checkChanges(activity.primary_settings_id);
  }

  afterCategoryStateChange(categoryId: string): void {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.checkService.getCategoryType(entity);

    if (!category || !category.primary_settings_id) {
      return;
    }

    const needFlag = this.checkService.isCategoryNeedUnforecastedFlag(
      categoryId,
      this.isPatientDriverAvailable,
      this.isSiteDriverAvailable,
      this.isCustomCurveAvailable
    );

    const hasFlag = !!this.forecastSettingsQuery.ui.getEntity(category.primary_settings_id)
      ?.unforecasted;

    if (needFlag !== hasFlag) {
      this.forecastSettingsStore.ui.update(category.primary_settings_id, {
        unforecasted: needFlag,
      });

      if (category.parent_category_id) {
        this.afterCategoryStateChange(category.parent_category_id);
      }
    }

    this.checkChanges(category.primary_settings_id);
  }

  // eslint-disable-next-line complexity
  changesToDefault(data: FullSettings) {
    const ids = this.checkService.getAllSubSettingIDs(data?.id || '');
    const milestone_category =
      this.timelinePhases.find((x) => x.firstMilestone?.id === data.period_start_milestone_id)
        ?.cat_id || null;

    const defaultValues = {
      driver: data?.driver || null,
      driver_setting_id: data?.driver_setting_id || null,
      forecast_method: data?.forecast_method || null,
      milestone_category,
      period_end_date: data?.period_end_date || null,
      period_end_milestone_id: data?.period_end_milestone_id || null,
      period_start_date: data?.period_start_date || null,
      period_start_milestone_id: data?.period_start_milestone_id || null,
    };

    this.forecastSettingsStore.update(data?.id, {
      ...defaultValues,
    });

    this.forecastSettingsService.update(data?.id, {
      ...defaultValues,
      override: data.override,
    });

    // If row has override or not this'll be update in here
    this.forecastSettingsStore.ui.update(data?.id || '', {
      showError: !(data?.forecast_method || data.override || data.driver),
      unforecasted: !(data?.forecast_method || data.override || data.driver),
    });

    this.checkChanges(data?.id || '');
    ids.forEach((id) => {
      this.edits.delete(id);
    });
    this.edits.delete(data?.id || '');
    this.defaultSubSettings.delete(data?.id || '');
  }

  checkChanges(primarySettingsId: string): void {
    const hasNoChanges = isEqual(
      this.initialSettings[primarySettingsId],
      this.forecastSettingsQuery.getEntity(primarySettingsId)
    );

    if (hasNoChanges) {
      this.edits.delete(primarySettingsId);
    }

    this.emitSaveCheck.emit(!!this.edits.size);
  }

  constructor() {
    // Allow AG Grid components to access this component
    this.context = {
      componentParent: this,
      forecastQuery: this.forecastQuery,
    };

    // Grid options
    this.gridOptions = this.ColumnsService.gridOptions;

    // Grid definitions
    const currentOrgCurrency = this.organizationQuery.getActive()?.currency || Currency.USD;

    this.columnDefs = this.ColumnsService.gridColumns(currentOrgCurrency);
  }

  getDiscountableTotal() {
    return this.activityQuery.getAll().reduce((total_cost, activity) => {
      if (
        (activity as FullActivity).activity_type === ActivityType.ACTIVITY_SERVICE &&
        activity.discount_flag
      ) {
        return total_cost + (activity.total_cost || 0);
      }

      return total_cost;
    }, 0);
  }

  getListBudgetVersionExpenses(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.organizationQuery
      .selectActiveId()
      .pipe(
        switchMap((vendorId) => {
          if (vendorId) {
            return this.gqlService.listBudgetVersionsExpenses$(
              [BudgetType.BUDGET_PRIMARY],
              vendorId
            );
          }

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ data, errors, success }) => {
        if (!this.changeDiscountTotalAmount.value) {
          this.emitChangeDiscountTotalAmount.emit(0);
          this.expensesAmount.amount = 0;
        }

        if (!this.changeDiscountTotalPercent.value) {
          this.emitChangeDiscountTotalPercent.emit(0);
          this.expensesAmount.amountPercent = 0;
        }

        if (success && data?.length) {
          const currentBudget = data.filter((budget) => budget.is_current)[0];

          this.budgetId = currentBudget.budget_version_id;

          this.selectedDiscountType.setValue(
            this.discountTypes.filter(
              (discountType) =>
                discountType.id ===
                (currentBudget.discount_type || DiscountType.DISCOUNT_PERCENTAGE)
            )[0]
          );

          let showDiscount = false;

          data[0].expense_amounts.forEach((budget) => {
            if (budget.amount_type === AmountType.AMOUNT_DISCOUNT) {
              this.expensesAmount.amount = budget.amount || 0;
              this.expensesAmount.amountPercent = budget.amount_perct || 0;

              showDiscount = showDiscount || !!budget.amount || !!budget.amount_perct;

              this.emitChangeDiscountTotalAmount.emit(budget.amount || 0);
              this.emitChangeDiscountTotalPercent.emit(budget.amount_perct || 0);
              this.discountableTotal = this.getDiscountableTotal();

              if (this.discountableTotal !== 0) {
                this.setDiscountAmounts();
              }
            }
          });

          this.emitHideDiscounts.emit(!showDiscount);
        } else {
          this.overlayService.error(errors);
        }
      });
  }

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return params.data.id;
  };

  onDiscardChanges() {
    this.rowData = cloneDeep(this.cloneRowData);
    this.gridValueChange.emit(this.rowData);

    const activitiesMap = keyBy(this.rowData, 'id');
    const activitiesByPrimarySettingMap = keyBy(this.rowData, 'primarySettingsId');

    // eslint-disable-next-line complexity
    this.edits.forEach((id) => {
      // sub rows are becoming defaults in this structure
      const editSetting = this.defaultSubSettings.get(id);

      if (editSetting && id) {
        this.changesToDefault(editSetting as unknown as FullSettings);
      } else {
        const row = activitiesMap[id] || activitiesByPrimarySettingMap[id];

        if (row) {
          const defaultValues = {
            driver: row.fullCategory?.primary_settings?.driver || null,
            driver_setting_id: row.fullCategory?.primary_settings?.driver_setting_id || null,
            forecast_method: row.fullCategory?.primary_settings?.forecast_method || null,
            id: row.fullCategory?.primary_settings.id,
            override: row.fullCategory?.primary_settings?.override,
            period_end_date: row.fullCategory?.primary_settings?.period_end_date || null,
            period_end_milestone_id:
              row.fullCategory?.primary_settings?.period_end_milestone_id || null,
            period_start_date: row.fullCategory?.primary_settings?.period_start_date || null,
            period_start_milestone_id:
              row.fullCategory?.primary_settings?.period_start_milestone_id || null,
          };

          this.changesToDefault(defaultValues as FullSettings);
        }
      }
    });
    // If override counts are changed, this func'll become to default values
    this.categoryService.defaultCategoryOverride();
    this.emitSaveCheck.emit(false);
    this.initialSettings = {};
    this.toggleService.reset();
  }

  // eslint-disable-next-line max-lines-per-function, complexity
  onDriverChange(
    driverType: DriverType | null,
    settings: FullSettings,
    mode: ForecastTableGridDriverSelectMode
  ): void {
    if (
      !this.defaultSubSettings.has(settings.id) &&
      !this.cloneRowData.some((x) => x.id === settings.id)
    ) {
      const subSettings = this.forecastSettingsQuery.getEntity(settings.id);

      this.defaultSubSettings.set(settings.id, subSettings as FullSettings);
    }

    this.edits.add(settings.id);
    this.setInitialSettings(settings.id);
    this.emitSaveCheck.emit(true);

    // Driver exists && (Driver site || Driver patient || Driver blended)
    if (
      driverType &&
      (driverType === DriverType.DRIVER_SITE ||
        driverType === DriverType.DRIVER_PATIENT ||
        driverType === DriverType.DRIVER_CUSTOM ||
        driverType === DriverType.DRIVER_SERVICES_BLENDED)
    ) {
      // Driver site and available || Driver patient and available || Driver services blended
      if (
        (driverType === DriverType.DRIVER_SITE && !this.isSiteDriverAvailable) ||
        (driverType === DriverType.DRIVER_PATIENT && !this.isPatientDriverAvailable) ||
        driverType === DriverType.DRIVER_SERVICES_BLENDED
      ) {
        this.forecastSettingsStore.update(settings.id, {
          driver: driverType,
        });

        if (driverType === DriverType.DRIVER_SITE) {
          this.forecastSettingsStore.ui.update(settings.id, () => ({
            errorMessage: 'Site Driver has not been added',
            showError: true,
          }));
        } else if (driverType === DriverType.DRIVER_PATIENT) {
          this.forecastSettingsStore.ui.update(settings.id, () => ({
            errorMessage: 'Patient Driver has not been added',
            showError: true,
          }));
        }
      } else {
        // Anything else
        this.forecastSettingsStore.update(settings.id, {
          driver: driverType,
          driver_setting_id: null,
          forecast_method: null,
          period_end_date: null,
          period_end_milestone_id: null,
          period_start_date: null,
          period_start_milestone_id: null,
        });

        this.forecastSettingsStore.ui.update(settings.id, () => ({
          errorMessage: '',
          showError: false,
        }));
      }
    } else {
      // Anything else
      this.forecastSettingsStore.update(settings.id, {
        driver: driverType,
        forecast_method: settings.forecast_method || ForecastMethodType.FORECAST_STRAIGHTLINE,
      });

      this.forecastSettingsStore.ui.update(settings.id, () => ({
        errorMessage: '',
        showError: false,
      }));
    }

    const uiForecast = this.forecastSettingsStore.ui.getValue();

    const isThereAnyOtherError = uiForecast?.ids?.some((id) => {
      return !!uiForecast?.entities?.[id]?.showError;
    });

    if (!isThereAnyOtherError && !this.successForecast) {
      this.emitSuccessForecast.emit(true);
    }

    if (mode.name === 'activity') {
      this.afterActivityStateChange(mode.id);
    } else {
      this.afterCategoryStateChange(mode.id);
    }
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridApi = api;
  }

  // Function type expression: ForecastTableGridMethodChange
  onMethodChange = (
    methodType: ForecastMethodType | null | string,
    primarySettingsId: string,
    mode: ForecastTableGridMethodSelectMode
  ): void => {
    this.edits.add(primarySettingsId);
    this.setInitialSettings(primarySettingsId);
    this.emitSaveCheck.emit(true);

    if (mode.prop === 'driver_setting') {
      this.forecastSettingsStore.update(primarySettingsId, {
        driver_setting_id: methodType as string,
      });
    } else {
      this.forecastSettingsStore.update(primarySettingsId, {
        forecast_method: methodType as ForecastMethodType,
      });
    }

    if (mode.name === 'activity') {
      this.afterActivityStateChange(mode.id);
    } else {
      this.afterCategoryStateChange(mode.id);
    }
  };

  // Function type expression: ForecastTableGridPeriodChange
  onPeriodChange = (settings: FullSettings, mode: ForecastTableGridDriverSelectMode): void => {
    const ref = this.overlayService.openPopup({
      content: ForecastTableGridPeriodTimeModalComponent,
      data: {
        settings,
        timelineMilestones: this.timelineMilestones,
        timelinePhases: this.timelinePhases,
      },
      settings: {
        header: 'Time Period',
        primaryButton: {
          action: (instance: ForecastTableGridPeriodTimeModalComponent | null) =>
            instance?.onSave(),
        },
      },
    });

    ref.afterClosed$.subscribe((overlayResponse) => {
      if (overlayResponse.data) {
        const { endDate, endMilestoneId, phaseId, startDate, startMilestoneId } =
          overlayResponse.data;

        this.edits.add(settings.id);
        this.emitSaveCheck.emit(true);
        this.setInitialSettings(settings.id);

        this.forecastSettingsStore.update(settings.id, {
          milestone_category: phaseId,
          period_end_date: endDate,
          period_end_milestone_id: endMilestoneId,
          period_start_date: startDate,
          period_start_milestone_id: startMilestoneId,
        });

        if (mode.name === 'activity') {
          this.afterActivityStateChange(mode.id);
        } else {
          this.afterCategoryStateChange(mode.id);
        }
      }
    });
  };

  onRowResize(): void {
    const defaultHeaderHeight = this.ColumnsService.gridOptions.headerHeight || 45;
    const defaultRowHeight = this.ColumnsService.gridOptions.rowHeight || 55;
    const defaultPadding = 10;
    const defaultTableHeight = 400;

    let newTableHeight =
      this.rowData.length * defaultRowHeight + defaultHeaderHeight + defaultPadding;

    newTableHeight += this.groupRowCount * defaultRowHeight;

    const newRowHeight = newTableHeight > defaultTableHeight ? newTableHeight : defaultTableHeight;

    this.gridHeight = `${newRowHeight}px`;
  }

  processChangeDiscountChanges(): void {
    if (
      this.expensesAmount.amount !== this.changeDiscountTotalAmount.value ||
      this.expensesAmount.amountPercent !== this.changeDiscountTotalPercent.value
    ) {
      this.emitSaveCheck.emit(true);
    }
  }

  processDiscountTypeChanges(value: typeof this.selectedDiscountType.value): void {
    if (value?.id === DiscountType.DISCOUNT_TOTAL) {
      this.emitChangeDiscountTotalPercent.emit(this.expensesAmount.amountPercent);
      this.emitChangeDiscountTotalPercentEnable.emit(false);
      this.emitChangeDiscountTotalAmountEnable.emit(true);
    } else if (value?.id === DiscountType.DISCOUNT_PERCENTAGE) {
      this.emitChangeDiscountTotalAmount.emit(this.expensesAmount.amount);
      this.emitChangeDiscountTotalAmountEnable.emit(false);
      this.emitChangeDiscountTotalPercentEnable.emit(true);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  saveChanges: ForecastTableGridSaveChange = async () => {
    for (const editId of this.edits) {
      const editSetting = this.forecastSettingsQuery.getEntity(editId);

      this.emitSuccessForecast.emit(true);
      this.forecastSettingsStore.ui.update(editId, () => ({ showError: false }));

      let success = true;

      if (
        this.checkService.isDriverTimeUnfilled(editSetting) ||
        this.checkService.isDriverSettingIdUnfilled(editSetting, DriverType.DRIVER_PATIENT) ||
        this.checkService.isDriverSettingIdUnfilled(editSetting, DriverType.DRIVER_SITE)
      ) {
        success = false;
        this.forecastSettingsStore.ui.update(editId, () => ({ showError: true }));
      }

      if (
        (editSetting?.driver === DriverType.DRIVER_PATIENT && !this.isPatientDriverAvailable) ||
        (editSetting?.driver === DriverType.DRIVER_SITE && !this.isSiteDriverAvailable)
      ) {
        this.emitIsPatientDriversLinkVisible.emit(
          editSetting?.driver === DriverType.DRIVER_PATIENT && !this.isPatientDriverAvailable
        );
        this.emitIsSiteDriversLinkVisible.emit(
          editSetting?.driver === DriverType.DRIVER_SITE && !this.isSiteDriverAvailable
        );
        this.emitSuccessForecast.emit(false);
        this.forecastSettingsStore.ui.update(editId, () => ({ showError: true }));

        return;
      }

      if (!success) {
        return;
      }
    }

    let forecastUpdated = false;

    const edits = [...this.edits];

    if (edits.length) {
      const ref = this.overlayService.loading();
      const responses = await batchPromises(edits, (p) =>
        this.forecastSettingsService.syncSetting(p)
      );

      if (responses.every((response) => !(response instanceof Error) && response?.success)) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: this.selectedVendor.value,
            entity_type: EntityType.ORGANIZATION,
            type: EventType.BUDGET_FORECAST_SETTINGS_UPDATED,
          })
        );

        if (success) {
          this.edits.forEach((x) => {
            const settings = this.forecastSettingsQuery.getEntity(x);

            this.defaultSubSettings.set(x, settings as FullSettings);
            this.cloneRowData = this.cloneRowData.map((y) => {
              if (x === y.primarySettingsId) {
                const cat = {
                  ...y.fullCategory,
                  primary_settings: settings,
                } as FullCategory;

                return {
                  ...y,
                  fullCategory: cat,
                };
              }

              return y;
            });
          });
          forecastUpdated = true;
          this.edits.clear();
        } else {
          forecastUpdated = false;
          this.overlayService.error(errors);
        }
      } else {
        forecastUpdated = false;
      }

      ref.close();
    }

    if (forecastUpdated) {
      this.overlayService.success('Changes successfully saved!');
      this.emitSaveCheck.emit(false);
      this.initialSettings = {};
    } else {
      this.overlayService.error('Something went wrong');
    }

    this.getListBudgetVersionExpenses();
  };

  setDiscountAmounts() {
    if (this.selectedDiscountType.value?.id === DiscountType.DISCOUNT_PERCENTAGE) {
      this.expensesAmount.amount =
        Math.round(this.discountableTotal * Math.abs(this.expensesAmount.amountPercent || 0)) /
        -100;

      this.emitChangeDiscountTotalAmount.emit(this.expensesAmount.amount || 0);
    } else if (this.selectedDiscountType.value?.id === DiscountType.DISCOUNT_TOTAL) {
      const amountPercent =
        Math.round((Math.abs(this.expensesAmount.amount) / this.discountableTotal) * 10000) / 100 ||
        0;

      this.expensesAmount.amountPercent = Number.isFinite(amountPercent) ? amountPercent : 0;

      this.emitChangeDiscountTotalPercent.emit(this.expensesAmount.amountPercent || 0);

      this.emitChangeDiscountTotalAmount.emit(
        Math.abs(this.changeDiscountTotalAmount.value) * -1 || 0
      );

      this.expensesAmount.amount = Math.abs(this.changeDiscountTotalAmount.value) * -1;
    }
  }

  setInitialSettings(id: string): void {
    if (!this.initialSettings[id]) {
      this.initialSettings[id] = this.forecastSettingsQuery.getEntity(id) as FullSettings;
    }
  }

  toggleActivityApplyAll(
    checked: boolean,
    activity: FullActivity,
    parentCategory: FullCategory
  ): void {
    const parentCategorySettings = parentCategory.primary_settings;
    const activitySettings = activity.primary_settings;

    const toggleSettings = (toggleChecked: boolean) => {
      return toggleChecked
        ? {
            driver: parentCategorySettings.driver,
            driver_setting_id: parentCategorySettings.driver_setting_id,
            forecast_method: parentCategorySettings.forecast_method,
            id: activitySettings.id,
            milestone_category: parentCategorySettings.milestone_category?.id,
            override: !activitySettings.override,
            period_end_date: parentCategorySettings.period_end_date,
            period_end_milestone_id: parentCategorySettings.period_end_milestone_id,
            period_start_date: parentCategorySettings.period_start_date,
            period_start_milestone_id: parentCategorySettings.period_start_milestone_id,
          }
        : {
            driver: null,
            driver_setting_id: null,
            forecast_method: null,
            id: activitySettings.id,
            milestone_category: null,
            override: !activitySettings.override,
            period_end_date: null,
            period_end_milestone_id: null,
            period_start_date: null,
            period_start_milestone_id: null,
          };
    };

    if (
      !this.defaultSubSettings.has(activitySettings.id) &&
      !this.cloneRowData.some((x) => x.id === activitySettings.id)
    ) {
      this.defaultSubSettings.set(
        activitySettings.id,
        activitySettings.driver_setting_id
          ? activitySettings
          : ({
              ...toggleSettings(!checked),
              override: activitySettings.override,
            } as FullSettings)
      );
    }

    this.edits.add(activitySettings.id);
    this.setInitialSettings(activitySettings.id);
    this.emitSaveCheck.emit(true);

    this.forecastSettingsService.update(activitySettings.id, toggleSettings(checked));

    this.forecastSettingsStore.ui.update(activitySettings.id, {
      unforecasted: false,
    });

    const overrideCount = !activitySettings.override ? 1 : -1;

    this.categoryService.updateCategoryOverride(activity.category_id, overrideCount);

    this.afterActivityStateChange(activity.id);
  }

  toggleCategoryApplyAll(
    checked: boolean,
    primarySettingsId: string,
    primarySettingsOverride: boolean,
    categoryId: string
  ): void {
    const ids = this.checkService.getAllSubSettingIDs(categoryId);
    const isCategoryIndeterminate = this.checkService.isCategoryIndeterminate(categoryId);

    this.setInitialSettings(primarySettingsId);

    ids.forEach((id) => {
      this.edits.add(id);
    });

    this.edits.add(primarySettingsId);

    if (checked || isCategoryIndeterminate) {
      ids.forEach((id) => {
        this.edits.delete(id);
      });
    }

    if (!this.defaultSubSettings.has(primarySettingsId)) {
      const settings = this.forecastSettingsQuery.getEntity(primarySettingsId);

      this.defaultSubSettings.set(primarySettingsId, settings as FullSettings);
    }

    this.emitSaveCheck.emit(true);

    const defaultValues = {
      driver: null,
      driver_setting_id: null,
      forecast_method: null,
      milestone_category: null,
      period_end_date: null,
      period_end_milestone_id: null,
      period_start_date: null,
      period_start_milestone_id: null,
    };

    this.forecastSettingsService.update(primarySettingsId, {
      override: !primarySettingsOverride,
      ...defaultValues,
    });

    this.forecastSettingsStore.update(ids, {
      ...defaultValues,
    });

    this.forecastSettingsStore.ui.update(primarySettingsId, {
      unforecasted: false,
    });

    this.afterCategoryStateChange(categoryId);
  }
}
